import { Ban, CalendarClock, ClipboardList, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { PpmpLineItemModal } from '../components/PpmpLineItemModal';
import { PpmpLineItemsModal } from '../components/PpmpLineItemsModal';
import { PpmpLineItemsPanel } from '../components/PpmpLineItemsPanel';
import { PpmpModal } from '../components/PpmpModal';
import { ppmpsApi } from '../ppmpsApi';
import type { PpmpDetail, PpmpLineItemDto } from '../ppmpTypes';
import { annualProcurementPlansApi } from '../procurementPlansApi';
import type { AnnualProcurementPlanDetail } from '../procurementPlanTypes';

type TabKey = 'info' | 'lines';

/**
 * One department's PPMP, and the procurement lines under it.
 *
 * **It LANDS ON LINES**, not Info — the thirteenth member of the document-profile landing
 * family: a reader opens a procurement plan to read what it intends to buy, which is its
 * CONTENT, rather than to check which PPA it hangs off. `Info` is still the first tab
 * DECLARED, so the strip's order and the development check are untouched.
 */
export function PpmpProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [ppmp, setPpmp] = useState<PpmpDetail | null>(null);
  const [plan, setPlan] = useState<AnnualProcurementPlanDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [running, setRunning] = useState(false);

  const [tab, setTab] = useState<TabKey>('lines');
  const [showDeleted, setShowDeleted] = useState(false);

  const [editing, setEditing] = useState(false);
  const [addingLines, setAddingLines] = useState(false);
  const [editingLine, setEditingLine] = useState<PpmpLineItemDto | null>(null);
  const [history, setHistory] = useState(false);
  const [confirming, setConfirming] = useState<
    { kind: 'delete-record' } | { kind: 'delete-line'; line: PpmpLineItemDto } | null
  >(null);

  /*
    `includeDeleted` follows the panel's own Show deleted, and it is what makes the line
    RESTORE reachable at all: the read filters deleted lines out by default, so without it
    nothing on any screen could name one to put back.
  */
  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      ppmpsApi
        .get(id, showDeleted, signal)
        .then(async (next) => {
          if (signal?.aborted) return;

          setPpmp(next);
          setError(null);

          // The PLAN, for the form the lines are filed through and for the breadcrumb. Fetched
          // after the record because its id is only known from it.
          try {
            const nextPlan = await annualProcurementPlansApi.get(
              next.annualProcurementPlanId,
              signal,
            );

            if (!signal?.aborted) setPlan(nextPlan);
          } catch {
            // Fails soft — the record is readable without it; only the editor needs the plan.
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this PPMP.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id, showDeleted],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setRunning(true);
    setError(null);

    try {
      await action();
      setConfirming(null);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Procurement Planning',
    {
      label: 'Annual Procurement Plan',
      to: '/procurement/procurement-planning/annual-procurement-plans',
    },
    ...(ppmp
      ? [
          {
            label: ppmp.appNumber,
            to: `/procurement/procurement-planning/annual-procurement-plans/${ppmp.annualProcurementPlanId}`,
          },
          {
            label: ppmp.orgUnitName,
            to: `/procurement/procurement-planning/annual-procurement-plans/${ppmp.annualProcurementPlanId}/departments/${ppmp.orgUnitId}`,
          },
        ]
      : []),
    ppmp?.ppmpNumber ?? 'PPMP',
  ];

  // A FAILED REFRESH must not replace the page — guarded on `error && !ppmp`.
  if (error && !ppmp) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (loading && !ppmp) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!ppmp) return null;

  const liveLines = ppmp.lineItems.filter((line) => line.isActive).length;

  /*
    What the plan is worth against what it was budgeted. NULL where no budgeted line is named —
    the honest state rather than a zero, and the banner says which.
  */
  const overBudget =
    ppmp.ppaLineItemAmount != null && ppmp.estimatedBudget > ppmp.ppaLineItemAmount;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="ppmp-profile"
        header={
          <ProfileHeader
            id="ppmp-profile-header"
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            title={ppmp.title}
            meta={[ppmp.ppmpNumber, ppmp.orgUnitName, `FY ${ppmp.fiscalYear}`]}
            status={
              ppmp.isActive ? (
                <StatusText tone="active">Active</StatusText>
              ) : (
                <StatusText tone="inactive">Deleted</StatusText>
              )
            }
            settings={{
              onEdit: () => setEditing(true),
              editPermission: 'ppmps.update',
              editDisabledReason: ppmp.isEditable
                ? null
                : 'The submission window for this plan has closed.',
              onHistory: () => setHistory(true),
              onDelete: ppmp.isActive ? () => setConfirming({ kind: 'delete-record' }) : undefined,
              deletePermission: 'ppmps.delete',
            }}
            createdBy={ppmp.createdByName}
            createdAt={ppmp.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="ppmp-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !ppmp.isActive ? (
            <ProfileBanner
              id="ppmp-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`${ppmp.ppmpNumber} is deleted. Its procurement lines are kept exactly as they are, so a restore brings the plan back with its contents.`}
              action={
                <PermissionGate permission="ppmps.delete">
                  <Button
                    id="ppmp-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void run(() => ppmpsApi.activate(ppmp.id))}
                    disabled={running}
                    loading={running}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : overBudget ? (
            /*
              AMBER: planning past the budgeted line is not refused — a planner may be pricing
              something the reviewer has yet to adjust — but it is the one thing on this page
              somebody should see before filing.
            */
            <ProfileBanner
              id="ppmp-profile-banner"
              tone="amber"
              icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
              message={`This plan intends to spend ${formatMoney(ppmp.estimatedBudget)} against a budgeted line of ${formatMoney(ppmp.ppaLineItemAmount ?? 0)}.`}
            />
          ) : !ppmp.isEditable ? (
            <ProfileBanner
              id="ppmp-profile-banner"
              tone="amber"
              icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
              message="The submission window for this annual procurement plan has closed, so this PPMP and its lines can no longer be changed."
            />
          ) : (
            <ProfileBanner
              id="ppmp-profile-banner"
              tone="slate"
              icon={<ClipboardList className="h-4 w-4" aria-hidden="true" />}
              message={`Filed under ${ppmp.appNumber} against ${ppmp.ppaNumber}${ppmp.ppaLineItemName ? ` — ${ppmp.ppaLineItemName}` : ''}.`}
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            label: (
              <>
                Line items <CountBadge count={liveLines} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
        panel={tab === 'lines' ? 'bleed' : 'cards'}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="ppmp-profile-plan"
              title="Plan"
              onEdit={() => setEditing(true)}
              editPermission="ppmps.update"
              editDisabledReason={
                ppmp.isEditable ? null : 'The submission window for this plan has closed.'
              }
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'PPMP number', value: ppmp.ppmpNumber, mono: true },
                  { label: 'Title', value: ppmp.title },
                  { label: 'Department', value: ppmp.orgUnitName },
                  { label: 'Annual procurement plan', value: ppmp.appNumber, mono: true },
                  { label: 'Fiscal year', value: String(ppmp.fiscalYear), mono: true },
                  {
                    label: 'Early procurement',
                    value: ppmp.coveredByEarlyProcurement ? 'Yes' : 'No',
                  },
                ]}
              />
            </ProfileCard>

            {/*
              A card over ANOTHER module's record — the PPA and its budgeted line belong to
              Investment Planning and are corrected there. That is the third case in
              `<ProfileCard>`'s exemption list, so it carries no Edit of those fields; which
              PPA this plan carries out IS this record's own field and is corrected on the card
              above, through the same form.
            */}
            <ProfileCard id="ppmp-profile-ppa" title="Budget">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'PPA', value: ppmp.ppaNumber, mono: true },
                  { label: 'Activity', value: ppmp.ppaName },
                  { label: 'Budgeted line', value: ppmp.ppaLineItemName },
                  {
                    label: 'Budgeted amount',
                    // NULL where no line is named — an em dash, not a zero.
                    value:
                      ppmp.ppaLineItemAmount != null
                        ? formatMoney(ppmp.ppaLineItemAmount)
                        : null,
                    mono: true,
                  },
                  {
                    label: 'Planned',
                    value: formatMoney(ppmp.estimatedBudget),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard
              id="ppmp-profile-schedule"
              title="Milestones"
              onEdit={() => setEditing(true)}
              editPermission="ppmps.update"
              editDisabledReason={
                ppmp.isEditable ? null : 'The submission window for this plan has closed.'
              }
              empty={
                ppmp.postingDate == null &&
                ppmp.openingOfBidsDate == null &&
                ppmp.noticeOfAwardDate == null &&
                ppmp.contractSigningDate == null
                  ? {
                      title: 'Nothing scheduled yet',
                      description:
                        'The four milestones are what a plan INTENDS, so a plan that has not scheduled itself is an ordinary state.',
                    }
                  : undefined
              }
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Posting',
                    value: ppmp.postingDate ? formatDateOnly(ppmp.postingDate) : null,
                    mono: true,
                  },
                  {
                    label: 'Opening of bids',
                    value: ppmp.openingOfBidsDate
                      ? formatDateOnly(ppmp.openingOfBidsDate)
                      : null,
                    mono: true,
                  },
                  {
                    label: 'Notice of award',
                    value: ppmp.noticeOfAwardDate
                      ? formatDateOnly(ppmp.noticeOfAwardDate)
                      : null,
                    mono: true,
                  },
                  {
                    label: 'Contract signing',
                    value: ppmp.contractSigningDate
                      ? formatDateOnly(ppmp.contractSigningDate)
                      : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="ppmp-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: ppmp.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(ppmp.createdAt)} className="font-mono">
                        {formatDateTime(ppmp.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: ppmp.updatedByName },
                  {
                    label: 'Updated',
                    value: ppmp.updatedAt ? (
                      <time dateTime={toIso(ppmp.updatedAt)} className="font-mono">
                        {formatDateTime(ppmp.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          /* A `bleed` panel owes its child a `p-4` wrapper. */
          <div className="flex h-full min-w-0 flex-col p-4">
            <PpmpLineItemsPanel
              ppmp={ppmp}
              showDeleted={showDeleted}
              onToggleDeleted={() => setShowDeleted((v) => !v)}
              onAdd={() => setAddingLines(true)}
              onEdit={(line) => setEditingLine(line)}
              onDelete={(line) => setConfirming({ kind: 'delete-line', line })}
              onRestore={(line) =>
                void run(() => ppmpsApi.restoreLineItem(ppmp.id, line.id))
              }
            />
          </div>
        )}
      </ProfileHeaderShell>

      {editing && plan ? (
        <PpmpModal
          open
          plan={plan}
          department={null}
          editing={ppmp}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {addingLines ? (
        <PpmpLineItemsModal
          open
          ppmp={ppmp}
          onClose={() => setAddingLines(false)}
          onSaved={() => {
            setAddingLines(false);
            load();
          }}
        />
      ) : null}

      {editingLine ? (
        <PpmpLineItemModal
          open
          ppmp={ppmp}
          line={editingLine}
          onClose={() => setEditingLine(null)}
          onSaved={() => {
            setEditingLine(null);
            load();
          }}
        />
      ) : null}

      {history ? (
        <HistoryLogsModal
          open
          onClose={() => setHistory(false)}
          module="ppmps"
          entityId={ppmp.id}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => setConfirming(null)}
          onConfirm={() =>
            void run(() =>
              confirming.kind === 'delete-record'
                ? ppmpsApi.deactivate(ppmp.id)
                : ppmpsApi.deleteLineItem(ppmp.id, confirming.line.id),
            )
          }
          title={confirming.kind === 'delete-record' ? 'Delete PPMP' : 'Delete procurement line'}
          danger
          requireConfirm
          requireCredential={confirming.kind === 'delete-record'}
          loading={running}
          id="ppmp-profile-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete-record' ? (
                <>
                  <p>
                    {ppmp.ppmpNumber} is hidden from its department's list and stops counting
                    toward the plan's planned total.
                  </p>
                  <p>
                    Its {liveLines} procurement line{liveLines === 1 ? '' : 's'} are kept exactly
                    as they are, so a restore brings the plan back with its contents. The PPMP
                    number is never reused.
                  </p>
                </>
              ) : (
                <p>
                  This line is hidden and stops counting toward the plan's total. Show deleted
                  brings it back into view, and it can be restored from there.
                </p>
              )}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
